import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, GBP, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const inputCls = "w-full px-4 py-3 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";
const FIELDS = [
  { k: "dob", label: "Date of birth", type: "date" },
  { k: "address", label: "Home address", type: "text" },
  { k: "contact_mobile", label: "Contact mobile number", type: "tel" },
  { k: "employment_status", label: "Employment status", type: "select", opts: ["Employed", "Self-employed", "Unemployed", "Student", "Other"] },
  { k: "previous_experience", label: "Previous experience", type: "textarea" },
  { k: "reason", label: "Reason for joining", type: "textarea" },
  { k: "support_needs", label: "Accessibility or support requirements", type: "textarea" },
];

export default function Apply() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [fullName, setFullName] = useState("");
  const [data, setData] = useState({});
  const [cohortDate, setCohortDate] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)); }, [courseId]);
  useEffect(() => { if (user?.name) setFullName(user.name); }, [user]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const cohorts = (course?.cohorts?.length ? course.cohorts : (course?.cohort_dates || []).map((d) => ({ id: d, date: d })));
  const hasCohorts = cohorts.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { toast.error("Please enter your full name — it appears on your certificate."); return; }
    if (!consentTerms) { toast.error("Please accept the terms and conditions."); return; }
    if (hasCohorts && !cohortDate) { toast.error("Please choose a workshop start date."); return; }
    if (!photoUrl) { toast.error("Please upload your photo — it appears on your certificate."); return; }
    setLoading(true);
    try {
      const application = { ...data, full_name: fullName.trim(), photo_url: photoUrl, consent_terms: consentTerms, consent_marketing: consentMarketing };
      const { data: enrolment } = await api.post("/enrolments", { course_id: courseId, application, documents: [], cohort_date: cohortDate });
      toast.success("Application submitted! Secure your place with a payment.");
      navigate(`/enrolment/${enrolment.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (JPG or PNG)."); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post("/media/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(res.url); toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed"); }
    finally { setPhotoUploading(false); e.target.value = ""; }
  };

  if (!course) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;

  const fmtDate = (d) => new Date(d + "T00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10" data-testid="apply-page">
      <p className="text-xs tracking-label uppercase font-semibold text-[var(--accent)]">Application</p>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-1 mb-1">{course.title}</h1>
      <p className="text-[var(--ink-2)] mb-8">{GBP(course.price)} · Deposit {GBP(course.deposit_amount)} · Starts {course.start_date}</p>

      <form onSubmit={submit} className="space-y-5 border border-[var(--line)] bg-white p-6" data-testid="application-form">
        <div data-testid="full-name-section">
          <label className="text-sm font-semibold mb-1.5 block">Full name <span className="text-[var(--accent)]">*</span></label>
          <p className="text-xs text-[var(--ink-2)] mb-2">Enter your name exactly as you'd like it to appear on your certificate.</p>
          <input data-testid="field-full_name" type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Jane Alexandra Smith" className={inputCls} />
        </div>
        {hasCohorts && (
          <div data-testid="cohort-picker">
            <label className="text-sm font-semibold mb-1.5 block">Choose your workshop date</label>
            <select data-testid="cohort-date-select" value={cohortDate} onChange={(e) => setCohortDate(e.target.value)} className={inputCls} required>
              <option value="">Select a date…</option>
              {cohorts.map((c) => {
                const parts = [fmtDate(c.date)];
                if (c.time) parts.push(c.time);
                if (c.location) parts.push(c.location);
                if (c.seats_left != null) parts.push(c.is_full ? "Full" : `${c.seats_left} seat${c.seats_left === 1 ? "" : "s"} left`);
                return <option key={c.id || c.date} value={c.date} disabled={c.is_full}>{parts.join(" · ")}</option>;
              })}
            </select>
          </div>
        )}
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="text-sm font-medium mb-1.5 block">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`field-${f.k}`} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} rows={3} className={inputCls} />
            ) : f.type === "select" ? (
              <select data-testid={`field-${f.k}`} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input data-testid={`field-${f.k}`} type={f.type} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className={inputCls} />
            )}
          </div>
        ))}

        <div>
          <label className="text-sm font-medium mb-1.5 block">Your photo <span className="text-[var(--accent)]">*</span></label>
          <p className="text-xs text-[var(--ink-2)] mb-2">A head-and-shoulders photo. This is printed on your certificate (top-right), so please use a clear, recent picture.</p>
          <div className="flex items-center gap-4">
            <div className="w-20 h-24 border border-[var(--line)] bg-[var(--alt)] flex items-center justify-center overflow-hidden shrink-0" data-testid="photo-preview">
              {photoUrl ? <img src={photoUrl} alt="Your photo" className="w-full h-full object-cover" /> : <span className="text-[10px] text-[var(--ink-2)]">No photo</span>}
            </div>
            <label data-testid="photo-upload" className="cursor-pointer text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)] transition-colors">
              {photoUploading ? "Uploading…" : photoUrl ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/*" className="hidden" disabled={photoUploading} onChange={onPhoto} />
            </label>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="consent-terms">
          <input type="checkbox" checked={consentTerms} onChange={(e) => setConsentTerms(e.target.checked)} className="mt-1 accent-[var(--accent)]" />
          I agree to the terms and conditions and refund policy.
        </label>
        <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="consent-marketing">
          <input type="checkbox" checked={consentMarketing} onChange={(e) => setConsentMarketing(e.target.checked)} className="mt-1 accent-[var(--accent)]" />
          Keep me updated about courses and opportunities (optional).
        </label>

        <button disabled={loading} data-testid="submit-application" className="w-full bg-[var(--accent)] text-white font-semibold py-4 hover:bg-[var(--accent-hover)] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? "Submitting…" : "Submit application & continue to payment"} <ArrowRight size={18} />
        </button>
      </form>
    </div>
  );
}
